import PortalUserResponse from "../models/PortalUserResponse";
import { useCallback } from "react";
import config from "./../config";
import enterpriseInformationApiSlice from "./enterpriseInformationApiSlice";
import {
    ClaimsSearch,
    ClaimsPaymentSearch,
    LeaveSearch,
    LeavePeriodProtectionSearch,
    LeaveSummarySearch,
    LeaveIntermittentSearch,
    LifeClaimSearch,
    LifePaymentSearch,
    AccommodationSearch,
} from "unmsght-report-package";

import DisabilitySnapshotsData from "../models/DisabilitySnapshotsData";

const dataviewApiSlice = enterpriseInformationApiSlice.injectEndpoints({
    endpoints: (builder) => {
        return {
            getClaimsSearch: builder.query({
                query: ({ filters }) => {
                    const baseParams = {
                        organizationId: filters.organizationId,
                        productGroupCd: filters.coverageType,
                        claimCoverageType: filters.claimCoverageTypes,
                        effectiveStartDt: filters.effectiveStartDate,
                        effectiveEndDt: filters.effectiveEndDate,
                        claimStatus: filters.claimStatus,
                        claimIds: filters.claimIds,
                        claimSourceSystemText: filters.claimSourceSystemText,
                        reportingGroup: filters.reportingGroup,
                        groupAgreementDivision: filters.groupAgreementDivision,
                        individualAgreementIdentifier:
                            filters.individualAgreementIdentifier,
                        startIndex: filters.startIndex,
                        startPage: filters.startPage,
                        count: filters.count,
                    };
                    const optionalArrayFields = ["effectiveDateNm"];

                    optionalArrayFields.forEach((key) => {
                        if (
                            Array.isArray(filters[key]) &&
                            filters[key].length > 0
                        ) {
                            baseParams[key] = filters[key];
                        }
                    });

                    return {
                        url: `claims/${process.env.REACT_APP_API_VERSION}${config.REACT_APP_ENV_NAME}`,
                        method: "GET",
                        params: baseParams,
                    };
                },
                invalidatesTags: (_) => ["claimsSearch"],
                transformResponse: (response, _meta, arg) => {
                    const formatMapping = {
                        Life: LifeClaimSearch.format,
                        Claims: LifeClaimSearch.format,
                        Snapshot: DisabilitySnapshotsData.format,
                    };

                    return (
                        formatMapping[arg.formatType] || ClaimsSearch.format
                    )(response, arg.formatType);
                },
            }),

            getClaimsPaymentSearch: builder.query({
                query: ({ filters }) => ({
                    url: `claims/${process.env.REACT_APP_API_VERSION}/payments${config.REACT_APP_ENV_NAME}`,
                    method: "GET",
                    params: {
                        effectiveStartDt: filters.effectiveStartDate,
                        effectiveEndDt: filters.effectiveEndDate,
                        organizationId: filters.organizationId,
                        productGroupCd: filters.coverageType,
                        claimCoverageType: filters.claimCoverageTypes,
                        paymentStatus: filters.paymentStatus,
                        groupAgreementDivision: filters.groupAgreementDivision,
                        reportingGroup: filters.reportingGroup,
                        startIndex: filters.startIndex,
                        startPage: filters.startPage,
                        count: filters.count,
                        includeComponentDetails: "Y",
                    },
                }),
                invalidatesTags: (_) => ["claimsPaymentSearch"],
                transformResponse: (response, _meta, arg) => {
                    const formatMapping = {
                        Life: LifePaymentSearch.format,
                        Payments: LifePaymentSearch.format,
                    };
                    return (
                        formatMapping[arg.formatType] ||
                        ClaimsPaymentSearch.format
                    )(response, arg.formatType);
                },
            }),
            getLeavesSearch: builder.query({
                query: ({ filters }) => {
                    const baseParams = {
                        organizationId: filters.organizationId,
                        coverageType: filters.coverageType,
                        effectiveStartDt: filters.effectiveStartDate,
                        effectiveEndDt: filters.effectiveEndDate,
                        startIndex: filters.startIndex,
                        startPage: filters.startPage,
                        count: filters.count,
                        leaveStatus: filters.leaveStatus,
                        reportingGroup: filters.reportingGroup,
                    };
                    const optionalArrayFields = [
                        "historicalLeaveInd",
                        "leavePeriodStatus",
                        "leaveScheduleType",
                        "protectionStatus",
                        "effectiveDateNm",
                    ];

                    optionalArrayFields.forEach((key) => {
                        if (
                            Array.isArray(filters[key]) &&
                            filters[key].length > 0
                        ) {
                            baseParams[key] = filters[key];
                        }
                    });

                    return {
                        url: `leaves/${process.env.REACT_APP_API_VERSION}${config.REACT_APP_ENV_NAME}`,
                        method: "GET",
                        params: baseParams,
                    };
                },
                invalidatesTags: (_) => ["LeavesSearch"],
                transformResponse: (response, _meta, arg) => {
                    const formatMapping = {
                        PeriodProtection: LeavePeriodProtectionSearch.format,
                        Summary: LeaveSummarySearch.format,
                        Detailed: LeaveSearch.format,
                        Accomodation: AccommodationSearch.format,
                        LeaveAsAccommodation: LeaveSearch.format,
                    };

                    return (
                        formatMapping[arg.formatType] || LeaveSearch.format
                    )(response, arg.formatType);
                },
            }),
            getIntermittentLeavesSearch: builder.query({
                query: ({ filters }) => ({
                    url: `leaves/${process.env.REACT_APP_API_VERSION}/intermittent-leaves${config.REACT_APP_ENV_NAME}`,
                    method: "GET",
                    params: {
                        organizationId: filters.organizationId,
                        coverageType: filters.coverageType,
                        effectiveStartDt: filters.effectiveStartDate,
                        effectiveEndDt: filters.effectiveEndDate,
                        startIndex: filters.startIndex,
                        startPage: filters.startPage,
                        count: filters.count,
                        leaveStatus: filters.leaveStatus,
                        leaveScheduleType: filters.leaveScheduleType,
                        historicalLeaveInd: filters.historicalLeaveInd,
                        reportingGroup: filters.reportingGroup,
                        leavePeriodStatus: filters.leavePeriodStatus,
                        protectionStatus: filters.protectionStatus,
                    },
                }),
                invalidatesTags: (_) => ["leaveIntermittentSearch"],
                transformResponse: (response, _meta, _arg) => {
                    return LeaveIntermittentSearch.format(response);
                },
            }),

            getPortalUserInfo: builder.query({
                query: ({ isfUserId, organizationId }) => ({
                    url: `portal-users/${process.env.REACT_APP_API_VERSION}/${isfUserId}${config.REACT_APP_ENV_NAME}`,
                    method: "GET",
                    params: {
                        organizationId: String(organizationId).toUpperCase(),
                    },
                }),
                invalidatesTags: (_) => ["portalUserInfo"],
                transformResponse: (response, _meta, _arg) => {
                    return PortalUserResponse.format(response);
                },
            }),
        };
    },
    overrideExisting: false,
});

export const {
    useLazyGetClaimsSearchQuery,
    useLazyGetClaimsPaymentSearchQuery,
    useLazyGetPortalUserInfoQuery: useLazyGetPortalUserInfoQueryInternal,
    useLazyGetLeavesSearchQuery,
    useLazyGetIntermittentLeavesSearchQuery,
} = dataviewApiSlice;

export const useLazyGetPortalUserInfoQuery = (options) => {
    const [trigger, result, lastPromiseInfo] =
        useLazyGetPortalUserInfoQueryInternal(options);
    const triggerPortalUser = useCallback(
        ({ isfUserId, organizationId }, preferCacheValue) => {
            const organizationIds = [organizationId]
                .flat()
                .flatMap((value) => String(value ?? "").split(","))
                .map((value) => value.trim().toUpperCase())
                .filter(Boolean);
            return trigger(
                {
                    isfUserId: String(isfUserId).trim().toUpperCase(),
                    organizationId: [...new Set(organizationIds)]
                        .sort()
                        .join(","),
                },
                preferCacheValue
            );
        },
        [trigger]
    );
    return [triggerPortalUser, result, lastPromiseInfo];
};

export default dataviewApiSlice;
